import { useState, type KeyboardEvent } from 'react'
import { skipToken } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { COUNTRY_CODE_OPTIONS, COUNTRY_CODE_US, type CountryCode } from '@klear/constants/countries'
import isString from 'lodash/isString'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@klear/ui/components/command'
import { trpc } from '@klear/ui/lib/trpc'
import type { PartyPick, PartyPickerKind } from '@/app/document-review/party-edit'

export type PartyPickerProps = {
  kind: PartyPickerKind
  traderId: number | null
  traderName: string
  seed: string
  disabled: boolean
  onPick: (pick: PartyPick) => void
  onCancel: () => void
}

type Choice = { value: string; label: string; detail: string | null; pick: PartyPick }

const CREATE_PARTY_VALUE = 'create-party'

export const COUNTRY_OPTIONS: ComboboxOption<CountryCode>[] = COUNTRY_CODE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.value,
  keywords: [isString(option.label) ? option.label : option.value]
}))

function matches(choice: Choice, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  return [choice.label, choice.detail ?? ''].join(' ').toLowerCase().includes(needle)
}

export function PartyPicker({ kind, traderId, traderName, seed, disabled, onPick, onCancel }: PartyPickerProps) {
  const [query, setQuery] = useState(seed)
  const [highlighted, setHighlighted] = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState<CountryCode>(COUNTRY_CODE_US)

  const scope = (wanted: PartyPickerKind) => (kind === wanted && traderId !== null ? { traderId } : skipToken)
  const customers = trpc.customer.list.useQuery(scope('customer'))
  const suppliers = trpc.supplier.list.useQuery(scope('supplier'))

  const choices: Choice[] =
    (kind === 'customer' ? customers.data : suppliers.data)?.flatMap((party) => {
      const id = party.identity.kind === 'saved' ? party.identity.id : null
      if (id === null) return []
      return [
        {
          value: `${kind}:${id}`,
          label: party.name,
          detail: party.address.country ?? null,
          pick: { kind: 'existing' as const, id, name: party.name }
        }
      ]
    }) ?? []

  const loading = customers.isLoading || suppliers.isLoading
  const trimmed = query.trim()
  const shown = choices.filter((choice) => matches(choice, query))
  const createPick: PartyPick | null = trimmed === '' ? null : { kind: 'create', name: trimmed, countryCode }
  const pickable = new Map<string, PartyPick>(shown.map((choice) => [choice.value, choice.pick]))
  if (createPick !== null) pickable.set(CREATE_PARTY_VALUE, createPick)
  const value = highlighted !== null && pickable.has(highlighted) ? highlighted : (pickable.keys().next().value ?? '')

  const onQueryChange = (next: string) => {
    setQuery(next)
    setHighlighted(null)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      if (disabled) return
      const pick = pickable.get(value)
      if (pick !== undefined) onPick(pick)
    }
  }

  return (
    <div className="mt-1.5 flex flex-col gap-1" onKeyDown={(event) => event.stopPropagation()}>
      <Command
        className="rounded-md border"
        loop
        shouldFilter={false}
        value={value}
        onValueChange={(next) => setHighlighted(next === '' ? null : next)}
        onKeyDown={onKeyDown}
      >
        <CommandInput
          autoFocus
          onFocus={(event) => event.currentTarget.select()}
          value={query}
          onValueChange={onQueryChange}
          placeholder={`Search ${traderName}’s ${kind}s…`}
          disabled={disabled}
        />
        <CommandList className="max-h-56">
          {shown.length === 0 && (
            <div className="text-muted-foreground px-2 py-3 text-center text-sm">
              {loading ? 'Loading…' : `No ${kind} matches.`}
            </div>
          )}
          {shown.length > 0 && (
            <CommandGroup heading={`${traderName}’s ${kind}s`}>
              {shown.map((choice) => (
                <CommandItem
                  key={choice.value}
                  value={choice.value}
                  disabled={disabled}
                  onSelect={() => onPick(choice.pick)}
                  className="justify-between"
                >
                  <span className="truncate">{choice.label}</span>
                  {choice.detail !== null && (
                    <span className="text-muted-foreground shrink-0 text-xs">{choice.detail}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup>
            <CommandItem
              value={CREATE_PARTY_VALUE}
              disabled={disabled || createPick === null}
              onSelect={() => createPick !== null && onPick(createPick)}
              className="justify-between"
            >
              <span className="flex items-center gap-2">
                <Plus aria-hidden />
                {trimmed === '' ? `Type a name to create a ${kind}` : `Create ${kind} “${trimmed}”`}
              </span>
              <span className="text-muted-foreground shrink-0 text-xs">{countryCode}</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
      <div className="text-muted-foreground flex flex-wrap items-center justify-end gap-2 text-xs">
        <span className="flex items-center gap-1.5">
          New {kind}’s country
          <Combobox<CountryCode>
            options={COUNTRY_OPTIONS}
            value={countryCode}
            onChange={(next) => {
              if (next !== '') setCountryCode(next)
            }}
            size="xs"
            className="h-6 w-20 text-xs"
            disabled={disabled}
          />
        </span>
      </div>
    </div>
  )
}
